import logger from 'ws-scripts/modules/logger';
import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from '@ddc/new-relic';
import { hysteriFetch, wrappers } from 'hysterics';
import { isBrowser } from 'ws-scripts/modules/environment';
import { applyDefaultSortBy } from './applyDefaultSortBy';
import { publishEvent } from './pubsub';
import {
	geoLocationObj,
	emptyFacetObj,
	emptyInventoryObj
} from '../global-constants';
import { updateDdcDataLayerFromWIS, setDataLayerTracking } from './tracking';
import { removeDeprecatedFacets } from './removeDeprecatedFacets';

const requestInventoryAndFacetsWrapper = hysteriFetch(
	'ws-inv-data-request-get-inventory-and-facets'
);

export const trackFetchDuration = (fetchType, endpoint) => {
	const numRequests = performance
		.getEntriesByType('resource')
		.filter((resource) => resource.name.includes(fetchType)).length;
	const timing = performance
		.getEntriesByType('resource')
		.filter((resource) => resource.name.includes(fetchType))[
		numRequests - 1
	];

	const message = `${fetchType} | ${endpoint} | duration: ${timing?.duration}, request: ${numRequests}`;
	logger.info(message);

	addNewRelicPageAction('WS INV DATA FETCH DURATION', {
		srpFetchType: fetchType,
		srpFetchEndpoint: endpoint,
		srpFetchCount: numRequests,
		srpFetchDuration: timing?.duration
	});
};

export const getWISRequestBody = ({
	requestData,
	prefs,
	invParams = undefined,
	sortBy = '',
	start = 0,
	flags = undefined
}) => {
	const {
		siteId,
		locale,
		deviceType,
		pageId,
		windowId,
		widgetName,
		pageAlias
	} = requestData;

	// Clone so the sortBy/start/geoZip assignments below never mutate the
	// caller's invParams or requestData.parameterMap (those objects are reused
	// across SSR diagnostics and subsequent requests).
	const inventoryParameters = {
		...(invParams || requestData.parameterMap || {})
	};

	// US1619575: New Car Boost A/B Testing
	if (
		isBrowser &&
		flags?.['ddc-ab-testing'] === 'EXPERIMENT' &&
		pageAlias?.endsWith('_USED')
	) {
		const cookieValue = document.cookie
			.split('; ')
			.find((row) => row.startsWith('ddc_ab_id'))
			?.split('=')[1];

		inventoryParameters.enableNewCarBoost = cookieValue;
	}

	if (sortBy.toString() !== '') {
		inventoryParameters.sortBy = [sortBy.toString()];
	}

	if (start.toString() !== '0') {
		inventoryParameters.start = [start.toString()];
	}

	if (
		inventoryParameters.geoZip &&
		inventoryParameters.geoZip.includes('null')
	) {
		inventoryParameters.geoZip = [''];
	}
	return JSON.stringify({
		siteId,
		locale,
		device: deviceType,
		pageAlias,
		pageId,
		windowId,
		widgetName,
		inventoryParameters,
		preferences: prefs,
		includePricing: true,
		...(flags && { flags })
	});
};

/**
 * Unified request for both inventory and facets data in a single API call
 * Reduces network requests and improves page load performance by fetching
 * both datasets simultaneously while preserving existing event publishing patterns
 *
 * @param {Object} invParams - Inventory parameters from URL/filters
 * @param {string} requestBody - JSON stringified request body containing search parameters
 * @param {string} eventType - Type of event triggering this request (search, facet, etc.)
 * @param {Object} eventData - Additional event data for tracking
 */
export const requestInventoryAndFacets = (
	invParams,
	requestBody,
	eventType,
	eventData
) => {
	(async () => {
		let facetResponse;
		let inventoryResponse;
		let unifiedResponse;
		let errorMessage;
		let errorStack;
		// DE748298: marks the synchronous stage in progress so a thrown error
		// (e.g. "Maximum call stack size exceeded") can be localized in NR.
		let failedStage = 'request';
		const jsBody = JSON.parse(requestBody);
		// Default to {} so the flag reads in the finally block (and below) can't
		// throw and mask the original error when a body arrives without `flags`.
		const { flags = {} } = jsBody;
		// Apply the default sort ONCE: applyDefaultSortBy returns a NEW object, so
		// the post-default sortBy must be read from here for both the fetch body
		// and the dataLayer tracking call below. Reading sortBy off the original
		// `invParams` instead would report `undefined` for an explicit user sort
		// (the cloned request body no longer mutates invParams) and for the
		// implicit geodist default — analytics-only, but a silent miss either way.
		const requestWithDefaults = applyDefaultSortBy(jsBody);

		try {
			publishEvent('ws-inv-data/inventory-request');

			// Make unified request to new endpoint
			failedStage = 'fetch';
			unifiedResponse = await requestInventoryAndFacetsWrapper(
				'/api/widget/ws-inv-data/getInventoryAndFacets',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(requestWithDefaults)
				},
				{
					[wrappers.timeout.contextKey]: {
						timeout: flags['ws-inv-data-wis-fetch-timeout']
					}
				}
			);

			if (
				unifiedResponse &&
				typeof unifiedResponse === 'object' &&
				(Array.isArray(unifiedResponse.facets) ||
					Array.isArray(unifiedResponse.inventory))
			) {
				failedStage = 'process-response';
				// ===================================================
				// SECTION 1: Process Facets Response
				// ===================================================
				facetResponse = {
					facets: unifiedResponse.facets,
					filters: unifiedResponse.filters,
					offSiteData: unifiedResponse.offSiteData,
					suggestedFilters: unifiedResponse.suggestedFilters,
					totalCount: unifiedResponse?.pageInfo?.totalCount
				};

				if (facetResponse.facets) {
					facetResponse.facets = removeDeprecatedFacets(
						facetResponse.facets
					);
					// Show the location facet whenever geo is enabled (geoData is
					// defined iff geoLocationEnabled), mirroring _hydrateFromSSR's
					// `if (this.geoData)` so the SSR and client-fallback paths render
					// the same facet UI on a no-geo page rather than diverging on
					// geoRadius presence.
					if (isBrowser && window.DDC?.InvData?.geoData) {
						facetResponse.facets.unshift(geoLocationObj);
					}
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.facets = facetResponse;
						window.DDC.InvData.appliedFilters =
							facetResponse?.filters || [];
						failedStage = 'publish-facets';
						publishEvent('ws-inv-data/facets');
					}
				} else {
					errorMessage = 'missing facet data';
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.facets = emptyFacetObj;
					}
				}

				// ===================================================
				// SECTION 2: Process Inventory Response
				// ===================================================
				inventoryResponse = {
					accounts: unifiedResponse.accounts,
					incentives: unifiedResponse.incentives,
					inventory: unifiedResponse.inventory,
					pageInfo: unifiedResponse.pageInfo
				};

				if (inventoryResponse.inventory) {
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.inventory = inventoryResponse;
						failedStage = 'publish-inventory';
						publishEvent('ws-inv-data/inventory');
					}
				} else {
					errorMessage = 'missing inventory data';
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.inventory = emptyInventoryObj;
					}
				}

				// ===================================================
				// SECTION 3: Fire Tracking Events
				// ===================================================
				// Only fire tracking if we have both facets and inventory data
				if (
					isBrowser &&
					window.DDC.InvData &&
					facetResponse.facets &&
					inventoryResponse.inventory
				) {
					const {
						pageInfo: { trackingData, ...pageInfo }
					} = inventoryResponse;

					failedStage = 'tracking';
					setDataLayerTracking(
						window.DDC.InvData.appliedFilters,
						window.DDC.InvData.sortBy,
						{
							pageInfo,
							trackingData
						},
						eventType,
						eventData
					);

					updateDdcDataLayerFromWIS(
						inventoryResponse,
						requestWithDefaults.inventoryParameters?.sortBy
					);
				}
			} else {
				// Handle null/undefined unified response or completely empty response
				errorMessage = 'missing inventory and facet data';
				if (isBrowser && window.DDC.InvData) {
					window.DDC.InvData.facets = emptyFacetObj;
					window.DDC.InvData.inventory = emptyInventoryObj;
				}
			}
		} catch (error) {
			// Log unified request failure
			errorMessage = error.message || error;
			errorStack = error.stack;
		} finally {
			// Track fetch duration if enabled
			if (flags['srp-track-fetch-resource-timing']) {
				trackFetchDuration('getInventoryAndFacets', 'WIS');
			}

			// Publish failure event if we don't have inventory
			if (!inventoryResponse?.inventory) {
				publishEvent('ws-inv-data/request-failed');
			}

			// Log error to New Relic if one occurred
			if (errorMessage) {
				setNewRelicCustomAttribute(
					'SRP ERROR',
					`ws-inv-data unified request failed: ${errorMessage}`
				);
				addNewRelicPageAction('WS INV DATA UNIFIED REQUEST FAILED', {
					errorMsg: errorMessage,
					errorStack,
					failedStage,
					hasFacets: !!facetResponse?.facets,
					hasInventory: !!inventoryResponse?.inventory
				});
			}
		}
	})();
};
